import type {
    ChallengeRuleConfigDto,
    ChallengeCollectiveRuleSetDto,
    MoneyValueDto,
    ChallengeGroupRulesUpdateDto
} from '@core/api/ChallengeClient';
import { autodisposer } from '@core/reactions';
import { injectTSDI } from '@core/tsdi';
import { FieldState, FormState } from 'formstate';
import { computed, makeObservable, observable, reaction } from 'mobx';
import { Component } from 'tsdi';
import { ChallengeDataStore } from '../../data-store';
import {
    formErrors,
    isFormStateDirty,
    makeFormClean
} from '@components/utils/form';
import { wrapInFormField } from '@core/forms';
import { required } from '@components/utils/validators';
import { bind } from 'lodash-decorators';
import { StepStatus } from '../../store';
import { Api } from '@core/api';
import { wrapRequest } from 'mobx-wrap-request';

export type ChallengeGroupRulesFormType = ReturnType<
    GroupRulesStore['generateForm']
>;

export type GroupRuleFormType = FormState<{
    activityPointsGoal: FieldState<number>;
    reward: FormState<{
        value: FieldState<number | undefined>;
        currencyCode: FieldState<string>;
    }>;
}>;

@Component
export class GroupRulesStore {
    constructor() {
        this.form = this.generateForm(
            this.challengeDataStore.challengeConfiguration?.rule
        );

        makeObservable(this, {
            form: observable,
            fields: computed,
            errors: computed,
            groupRulesList: computed
        });
    }

    public form: ChallengeGroupRulesFormType;
    public state: StepStatus = StepStatus.INCOMPLETE;

    private get challengeClient() {
        return injectTSDI(Api).challengeClient;
    }

    private get challengeDataStore() {
        return injectTSDI(ChallengeDataStore);
    }

    private generateForm(configuration?: ChallengeRuleConfigDto) {
        const groupRules = configuration?.groupRules || [];

        return new FormState({
            groupRules: new FieldState<GroupRuleFormType[]>(
                groupRules.map((rule) => this.createGroupRuleForm(rule))
            )
        });
    }

    private createGroupRuleForm(
        rule?: ChallengeCollectiveRuleSetDto
    ): GroupRuleFormType {
        return new FormState({
            activityPointsGoal: new FieldState<number>(
                rule?.activityPointsGoal || 0
            ).validators(required(), this.positiveNumberValidator),
            reward: new FormState({
                value: new FieldState<number | undefined>(
                    rule?.reward?.value || undefined
                ).validators((val) =>
                    val ? this.positiveNumberValidator(val) : false
                ),
                currencyCode: new FieldState<string>(
                    rule?.reward?.currencyCode || 'EUR'
                ).validators(required())
            })
        });
    }

    @bind
    private positiveNumberValidator(val: number): string | undefined {
        if (!val || val <= 0) {
            return 'Value must be greater than 0';
        }
        return undefined;
    }

    public get fields() {
        return {
            groupRules: wrapInFormField(this.form.$.groupRules)
        };
    }

    public get groupRulesList() {
        return this.form.$.groupRules.value;
    }

    public get errors() {
        return formErrors(this.form);
    }

    @bind
    public addGroupRule() {
        const newRule = this.createGroupRuleForm();
        const currentRules = [...this.form.$.groupRules.value];
        currentRules.push(newRule);
        this.form.$.groupRules.onChange(currentRules);
    }

    @bind
    public removeGroupRule(index: number) {
        const currentRules = [...this.form.$.groupRules.value];
        if (index >= 0 && index < currentRules.length) {
            currentRules.splice(index, 1);
            this.form.$.groupRules.onChange(currentRules);
        }
    }

    public generateRequestData(
        form: ChallengeGroupRulesFormType
    ): ChallengeCollectiveRuleSetDto[] {
        if (form.$.groupRules.value.length === 0) {
            return [];
        }
        const result = form.$.groupRules.value.map((ruleForm) => {
            const activityPointsGoal = ruleForm.$.activityPointsGoal.value;
            const rewardValue = ruleForm.$.reward.$.value.value;
            const rewardCurrencyCode = ruleForm.$.reward.$.currencyCode.value;

            const reward: MoneyValueDto | undefined = rewardValue
                ? {
                      value: rewardValue,
                      currencyCode: rewardCurrencyCode
                  }
                : undefined;

            return {
                activityPointsGoal,
                reward
            };
        });

        return result;
    }

    public get formIsDirty() {
        return (
            isFormStateDirty(this.form.$) ||
            this.form.$.groupRules.value.some((rule) =>
                isFormStateDirty(rule.$)
            )
        );
    }

    public updateGroupRules() {
        return this.challengeGroupRulesRequest.request({
            challengeId: this.challengeDataStore.challengeId!,
            data: this.generateRequestUpdateRulesData(this.form)
        });
    }

    public challengeGroupRulesRequest = wrapRequest(
        async ({
            challengeId,
            data
        }: {
            challengeId: number;
            data: ChallengeGroupRulesUpdateDto;
        }) => {
            const result =
                await this.challengeClient?.support.challengeSupportControllerUpdateGroupRules(
                    challengeId,
                    data
                );

            return result?.data;
        }
    );

    public generateRequestUpdateRulesData(
        form: ChallengeGroupRulesFormType
    ): ChallengeGroupRulesUpdateDto {
        return {
            groupRules: this.generateRequestData(form)
        };
    }

    @bind
    public async onChangeHandle() {
        const validation = await this.form.validate();
        const groupRulesValidation = await Promise.all(
            this.form.$.groupRules.value.map((rule) => rule.validate())
        );

        if (
            validation.hasError ||
            groupRulesValidation.some((v) => v.hasError)
        ) {
            this.state = StepStatus.ERROR;
        } else {
            this.state = StepStatus.MODIFIED;
        }
    }

    @bind
    public async updateForm(configuration?: ChallengeRuleConfigDto) {
        this.form = this.generateForm(configuration);
        const validation = await this.form.validate();
        const groupRulesValidation = await Promise.all(
            this.form.$.groupRules.value.map((rule) => rule.validate())
        );

        if (
            validation.hasError ||
            groupRulesValidation.some((v) => v.hasError)
        ) {
            this.state = StepStatus.ERROR;
            return;
        }
        this.state = StepStatus.COMPLETED;
        makeFormClean(this.form);
        this.form.$.groupRules.value.forEach((rule) => makeFormClean(rule));
        return;
    }

    @autodisposer.tsdi
    public initialize() {
        return [
            reaction(
                () => this.challengeDataStore.challengeConfiguration,
                async (configuration) => {
                    this.updateForm(configuration?.rule);
                }
            )
        ];
    }
}
